import React, { useState } from "react";
import tw, { styled } from "twin.macro";
import Header from "components/headers/light.js";
import Footer from "components/footers/SimpleFiveColumn.js";
import AnimationRevealPage from "helpers/AnimationRevealPage";

import VectorDesign from "images/Vector1.svg";
import GrayDesign from "images/blueDots.svg";
import LineDesign from "images/halfRedCircle.png";
import FullRed from "images/halfRedGray.svg";

// -----------------------------
// Styled Components
// -----------------------------

const Section = tw.section`
  bg-gradient-to-br from-white to-gray-100 py-20 px-4 relative overflow-hidden
`;

const Container = tw.div`
  max-w-6xl mx-auto relative z-10
`;

const HeadingWrap = tw.div`text-center mb-16`;

const PageTitle = tw.h1`
  text-4xl md:text-5xl font-bold text-gray-800 mb-4
`;

const Vector = tw.img`w-20 mx-auto mb-4`;

const SubText = tw.p`
  text-xl text-gray-600 max-w-2xl mx-auto
`;

const Box = tw.div`
  bg-white rounded-2xl shadow-lg overflow-hidden mb-16
`;

const BoxInner = tw.div`p-8 md:p-12`;

const SectionTitle = tw.h2`
  text-2xl font-bold text-gray-800 mb-4 border-b-2 border-gray-200 pb-2
`;

const Paragraph = tw.p`text-gray-700 leading-relaxed mb-4`;

const BulletList = tw.ul`space-y-2 text-gray-700 mb-4 list-decimal pl-6`;

const AlphaList = tw.ul`space-y-2 text-gray-700 mb-4`;

const ContactBox = tw.div`text-center mt-10`;

const ContactButton = tw.a`
  inline-block bg-[#3f6c68] hover:bg-[#355a56] text-white font-bold
  py-4 px-8 rounded-full text-lg transition-colors duration-300
  shadow-lg hover:shadow-xl
`;

const FloatingImg = styled.img(({ position }) => [
  tw`absolute opacity-75 transition-transform duration-200`,
  position === "left" && tw`top-[150px] left-0`,
  position === "right" && tw`bottom-[60px] right-[60px]`,
  position === "mid" && tw`top-1/2 right-[10%] opacity-50`,
]);

// -----------------------------
// Component
// -----------------------------

const TermsCondition = () => {
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    const x = e.clientX - window.innerWidth / 2;
    const y = e.clientY - window.innerHeight / 2;
    setMouse({ x, y });
  };

  return (
    <AnimationRevealPage>
      <Header />

      <Section onMouseMove={handleMouseMove}>
        {/* Floating Decorative Images */}
        <FloatingImg
          src={GrayDesign}
          position="left"
          style={{
            transform: `translate(${mouse.x * 0.03}px, ${mouse.y * 0.03}px)`,
          }}
        />

        <FloatingImg
          src={LineDesign}
          position="right"
          style={{
            transform: `translate(${mouse.x * -0.03}px, ${mouse.y * -0.03}px)`,
          }}
        />

        <FloatingImg
          src={FullRed}
          position="mid"
          style={{
            transform: `translate(${mouse.x * -0.02}px, ${
              mouse.y * -0.02
            }px) rotate(45deg)`,
          }}
        />

        {/* MAIN CONTENT */}
        <Container>
          <HeadingWrap>
            <PageTitle>Terms & Conditions</PageTitle>
            <Vector src={VectorDesign} />
            <SubText style={{ fontWeight: 700 }}>
              Welcome To CA Vijender Aggarwal Classes
            </SubText>
            <SubText style={{ textAlign: "justify" }}>
              These Terms and Conditions govern your use of our website and
              services. By accessing or using this website, you agree to comply
              with and be bound by the following terms.
            </SubText>
          </HeadingWrap>

          {/* ------------------ REFUND SECTION ------------------ */}
          <Box>
            <BoxInner>
              <SectionTitle>1. Definitions</SectionTitle>

              <BulletList>
                <li>
                  <span style={{ fontWeight: 700, textAlign: "justify" }}>
                    “You”, “User”, “Client”
                  </span>{" "}
                  refers to any person accessing this website.
                </li>
                <li>
                  <span style={{ fontWeight: 700, textAlign: "justify" }}>
                    “We”, “Our”, “Us”
                  </span>{" "}
                  refers to CA Vijender Aggarwal Classes and its associated
                  services.
                </li>
                <li>
                  <span style={{ fontWeight: 700, textAlign: "justify" }}>
                    “Website”
                  </span>{" "}
                  refers to our official website and all related pages.
                </li>
                <li>
                  <span style={{ fontWeight: 700, textAlign: "justify" }}>
                    “Services”
                  </span>{" "}
                  include courses, study materials, consultancy, educational
                  content, and any other offerings.
                </li>
              </BulletList>
            </BoxInner>
          </Box>

          {/* ------------------ RETURN CONDITIONS ------------------ */}
          <Box>
            <BoxInner>
              <SectionTitle>2. Acceptance Of Terms</SectionTitle>

              <Paragraph>
                By accessing this website, you confirm that you are legally
                capable of entering into a binding agreement and that you accept
                these Terms & Conditions in full.
              </Paragraph>
            </BoxInner>
          </Box>

          {/* ------------------ CANCELLATION POLICY ------------------ */}
          <Box>
            <BoxInner>
              <SectionTitle>3. Use Of Website</SectionTitle>

              <Paragraph>
                You agree to use this website only for lawful purposes. You must
                not:
              </Paragraph>

              <BulletList>
                <li>Misuse the website or its content</li>
                <li>
                  Attempt to gain unauthorized access to any part of the website
                </li>
                <li>Upload or transmit harmful or malicious content</li>
                <li>Use the website for fraudulent or illegal activities</li>
              </BulletList>
            </BoxInner>
          </Box>

          {/* ------------------ TERMS & CONDITIONS ------------------ */}
          <Box>
            <BoxInner>
              <SectionTitle>4. Intellectual Property Rights</SectionTitle>

              <Paragraph>
                Unless otherwise stated, all content on this website, including
                text, videos, graphics, logos, study materials, and course
                content, is the intellectual property of
                <span style={{ fontWeight: 700, textAlign: "justify" }}>
                  CA Vijender Aggarwal Classes.
                </span>{" "}
                with:
              </Paragraph>

              <Paragraph>
                You must
                <span style={{ fontWeight: 700, textAlign: "justify" }}>
                  not:
                </span>{" "}
                with:
              </Paragraph>

              <BulletList>
                <li>Republish content without permission</li>
                <li>Sell, rent, or sub-license website material</li>
                <li>Copy or reproduce content for commercial use</li>
                <li>Redistribute content without written consent</li>
              </BulletList>

              <Paragraph>
                Content may be accessed for{" "}
                <span style={{ fontWeight: 700, textAlign: "justify" }}>
                  personal and non-commercial use only.
                </span>
              </Paragraph>
            </BoxInner>
          </Box>
          {/* Contact Support */}
          <Box>
            <BoxInner>
              <SectionTitle>5. Payments & No Refund Policy</SectionTitle>

              <Paragraph>
                All payments made on this website are
                <span style={{ fontWeight: 700, textAlign: "justify" }}>
                  final and non-refundable.
                </span>{" "}
                Once a service or product is purchased, no cancellation, refund,
                or reversal will be allowed under any circumstances.
              </Paragraph>
            </BoxInner>
          </Box>

          <Box>
            <BoxInner>
              <SectionTitle>6. User Comments & Feedback</SectionTitle>

              <Paragraph>
                Certain sections of the website may allow users to post comments
                or feedback.
              </Paragraph>
              <Paragraph>By posting content, you confirm that:</Paragraph>

              <BulletList>
                <li>You own the rights to the content</li>
                <li>The content is not defamatory, offensive, or illegal</li>
                <li>The content does not violate any third-party rights</li>
              </BulletList>
              <Paragraph>
                We reserve the right to{" "}
                <span style={{ fontWeight: 700, textAlign: "justify" }}>
                  {" "}
                  monitor, edit, or remove
                </span>{" "}
                any content that violates these Terms.
              </Paragraph>
              <Paragraph>
                You grant us a{" "}
                <span style={{ fontWeight: 700, textAlign: "justify" }}>
                  {" "}
                  non-exclusive license{" "}
                </span>{" "}
                to use, reproduce, and display your comments where required.
              </Paragraph>
            </BoxInner>
          </Box>
          <Box>
            <BoxInner>
              <SectionTitle>7. Cookies</SectionTitle>

              <Paragraph>
                Our website uses cookies to improve user experience and website
                functionality. By using our website, you agree to our use of
                cookies as described in our Privacy Policy.
              </Paragraph>
            </BoxInner>
          </Box>
          <Box>
            <BoxInner>
              <SectionTitle>8. Hyperlinking To Our Website</SectionTitle>

              <Paragraph>
                Certain organizations (government bodies, search engines,
                educational institutions, etc.) may link to our website without
                prior approval.
              </Paragraph>
              <Paragraph>
                Other organizations wishing to link must seek written
                permission. Use of our logo or branding without authorization is
                strictly prohibited.
              </Paragraph>
            </BoxInner>
          </Box>
          <Box>
            <BoxInner>
              <SectionTitle>9. iFrames</SectionTitle>

              <Paragraph>
                You may not create frames around our website pages that alter
                the visual presentation without prior written consent.
              </Paragraph>
            </BoxInner>
          </Box>
          <Box>
            <BoxInner>
              <SectionTitle>10. Content Liability</SectionTitle>

              <Paragraph>
                We are not responsible for any content that appears on
                third-party websites linking to us. You agree to indemnify and
                defend us against any claims arising from your website or
                content.
              </Paragraph>
            </BoxInner>
          </Box>
          <Box>
            <BoxInner>
              <SectionTitle>11. Privacy Policy</SectionTitle>

              <Paragraph>
                Your use of this website is also governed by our Privacy Policy.
                Please review it carefully to understand how we collect and use
                your information.
              </Paragraph>
            </BoxInner>
          </Box>
          <Box>
            <BoxInner>
              <SectionTitle>12. Reservation of Rights</SectionTitle>

              <Paragraph>We reserve the right to:</Paragraph>
              <BulletList>
                <li>Modify these Terms & Conditions at any time</li>
                <li>
                  Restrict or terminate access to the website without prior
                  notice
                </li>
                <li>Request removal of unauthorized links to our website</li>
              </BulletList>
              <Paragraph>
                Continued use of the website implies acceptance of updated
                terms.
              </Paragraph>
            </BoxInner>
          </Box>
          <Box>
            <BoxInner>
              <SectionTitle>13. Disclaimer</SectionTitle>

              <Paragraph>
                The information and services on this website are provided “as
                is” without warranties of any kind.
              </Paragraph>
              <Paragraph>We do not guarantee:</Paragraph>
              <BulletList>
                <li>Accuracy or completeness of information</li>
                <li>Continuous availability of the website</li>
                <li>That services will meet all user expectations</li>
              </BulletList>
              <Paragraph>
                To the maximum extent permitted by law, we shall not be liable
                for any loss or damage arising from the use of this website or
                services.
              </Paragraph>
            </BoxInner>
          </Box>
        </Container>
      </Section>

      <Footer />
    </AnimationRevealPage>
  );
};

export default TermsCondition;
